import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead, getUnreadCount } from './notificationApi';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export function NotificationProvider({ children }) {
  const { userProfile } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchNotifications = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      if (userProfile && userProfile.id) {
        // Convert params to proper filter format
        // If read is explicitly set to empty string or null, don't filter by read status (show all)
        const filter = {
          read: params.read !== undefined && params.read !== '' && params.read !== null ? params.read : undefined,
          category: params.category || undefined,
          priority: params.priority || undefined
        };
        
        const notificationsData = await getNotifications(userProfile.id, filter);
        setNotifications(Array.isArray(notificationsData) ? notificationsData : []);
        
        // Get unread count separately
        const count = await getUnreadCount();
        setUnreadCount(count);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      if (err?.status === 401) {
        window.location.href = '/';
        return;
      }
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [userProfile]);

  const markNotificationRead = async (id) => {
    try {
      await markNotificationAsRead(id);
      // Immediately update local state for better UX
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, read_at: new Date().toISOString() } : n
      ));
      // Update unread count immediately
      setUnreadCount(prev => Math.max(0, prev - 1));
      // Wait a moment for backend to process, then refresh for consistency
      setTimeout(() => {
        fetchNotifications();
      }, 300);
    } catch (err) {
      console.error('Error marking notification as read:', err);
      if (err?.status === 401) {
        window.location.href = '/';
        return;
      }
      setError(err);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      console.log('🔄 Marking all notifications as read...');
      const result = await markAllNotificationsAsRead();
      console.log('✅ Mark all as read result:', result);
      
      if (!result) {
        console.warn('⚠️ Mark all as read returned false');
        return;
      }
      
      // Immediately update local state for better UX
      const now = new Date().toISOString();
      setNotifications(prev => prev.map(n => ({ ...n, read_at: now })));
      setUnreadCount(0);
      console.log('✅ Updated local state - all notifications marked as read');
      
      // Wait a moment for backend to process, then refresh from server for consistency
      setTimeout(async () => {
        console.log('🔄 Refreshing notifications from server...');
        // Fetch all notifications (read and unread) - they'll be grayed out if read
        await fetchNotifications(); // Fetch all notifications without any read filter
        
        // Refresh unread count from server
        const updatedCount = await getUnreadCount();
        setUnreadCount(updatedCount);
        console.log('✅ Final unread count after refresh:', updatedCount);
        
        // Verify notifications have read_at set
        const currentNotifications = await getNotifications(userProfile.id, {});
        const allRead = currentNotifications.every(n => n.read_at);
        console.log('✅ All notifications read:', allRead, 'Total:', currentNotifications.length);
      }, 800); // Increased timeout to ensure backend has processed
    } catch (err) {
      console.error('❌ Error marking all notifications as read:', err);
      if (err?.status === 401) {
        window.location.href = '/';
        return;
      }
      setError(err);
    }
  };

  const deleteNotification = async (id) => {
    try {
      // Get the notification to check if it was unread
      const notification = notifications.find(n => n.id === id);
      const wasUnread = notification && !notification.read_at;
      
      // Remove from local state
      setNotifications(prev => prev.filter(n => n.id !== id));
      
      // Update unread count immediately if the deleted notification was unread
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      // Refresh from server to ensure consistency
      fetchNotifications();
    } catch (err) {
      console.error('Error deleting notification:', err);
      if (err?.status === 401) {
        window.location.href = '/';
        return;
      }
      setError(err);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, loading, error, fetchNotifications, markNotificationRead, markAllNotificationsRead, deleteNotification }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
} 